import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { canGoLive } from '@theme/motion-lifecycle';

/**
 * @typedef {object} FeatureExplorerRefs
 * @property {HTMLElement} list - The pill list (card + icon column on mobile)
 * @property {HTMLElement[]} items - One list item per feature
 * @property {HTMLButtonElement[]} toggles - The pill buttons
 * @property {HTMLElement[]} panels - The card content shown for the active feature
 * @property {HTMLElement[]} slides - One media slide per feature
 * @property {HTMLElement[]} [dots] - Progress dots over the media
 * @property {HTMLButtonElement} [prevButton] - Desktop only; shrinks away on the first feature
 * @property {HTMLButtonElement} [nextButton] - Desktop only; shrinks away on the last feature
 * @property {HTMLButtonElement} [pauseButton] - Shown when videos loop
 */

const SWIPE_THRESHOLD = 40;
const IMAGE_HOLD = 5000;
const ANIMATION_DURATION = 400;
const mediaQueryDesktop = matchMedia('(min-width: 990px)');

/**
 * Builds a CSS `linear()` easing from a damped spring, so the card grows with a little settle,
 * like a physics spring rather than a fixed curve.
 * The curve is the spring's full settle, played back over `duration` ms.
 * @param {number} stiffness
 * @param {number} damping
 * @param {number} duration
 * @returns {{ easing: string, duration: number }}
 */
function springEasing(stiffness, damping, duration) {
  const omega = Math.sqrt(stiffness);
  const zeta = damping / (2 * omega);
  const omegaD = omega * Math.sqrt(1 - zeta * zeta);
  const settle = -Math.log(0.001) / (zeta * omega);

  if (!CSS.supports('transition-timing-function', 'linear(0, 1)')) {
    return { easing: 'cubic-bezier(0.34, 1.3, 0.64, 1)', duration };
  }

  const steps = 48;
  const points = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * settle;
    const x =
      1 - Math.exp(-zeta * omega * t) * (Math.cos(omegaD * t) + ((zeta * omega) / omegaD) * Math.sin(omegaD * t));
    points.push(i === steps ? '1' : x.toFixed(4));
  }
  return { easing: `linear(${points.join(', ')})`, duration };
}

// Opening springs past its size and settles; closing eases in without overshoot, so the pill never clips its label
const SPRING = springEasing(260, 24, ANIMATION_DURATION);
const COLLAPSE = { easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', duration: ANIMATION_DURATION };
// The exit half of the same bounce: swells slightly, then drops away
const BOUNCE_OUT = { easing: 'cubic-bezier(0.6, -0.28, 0.735, 0.045)', duration: ANIMATION_DURATION };

/**
 * Feature explorer: one feature open at a time. Choosing a pill grows it into a card and
 * crossfades the media stage to that feature's video, which plays while the section is on screen.
 *
 * @extends {Component<FeatureExplorerRefs>}
 */
class FeatureExplorerComponent extends Component {
  requiredRefs = ['list', 'items', 'toggles', 'panels', 'slides'];

  #active = 0;
  #inView = false;
  #userPaused = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {{ x: number, y: number } | null} */
  #touchStart = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #listWidth = 0;
  /** Moves on when a video ends (or an image has held), until a visitor changes feature or pauses */
  #autoAdvance = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #imageTimer;

  connectedCallback() {
    super.connectedCallback();

    const { toggles } = this.refs;
    this.#active = Math.max(
      0,
      toggles.findIndex((toggle) => toggle.getAttribute('aria-expanded') === 'true')
    );
    this.#syncNav(false);

    this.#autoAdvance = this.hasAttribute('data-auto-advance') && !prefersReducedMotion();
    for (const video of this.#videos) video.addEventListener('ended', this.#onVideoEnded);

    // Mobile: reserve the tallest card's height so the media doesn't resize between features
    this.#resizeObserver = new ResizeObserver(() => {
      const width = this.refs.list.clientWidth;
      if (width === this.#listWidth) return;
      this.#listWidth = width;
      this.#reserveCardHeight();
    });
    this.#resizeObserver.observe(this.refs.list);
    document.fonts?.ready.then(() => this.#reserveCardHeight());

    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#inView = Boolean(entry?.isIntersecting);
        this.#syncPlayback();
      },
      { threshold: 0.35 }
    );
    this.#observer.observe(this);

    // Theme editor: open the block the merchant selects
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('touchstart', this.#onTouchStart);
    this.removeEventListener('touchend', this.#onTouchEnd);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    for (const video of this.#videos) video.removeEventListener('ended', this.#onVideoEnded);
    clearTimeout(this.#imageTimer);
  }

  /**
   * Opens the feature at `index` (clamped to the list). Called by visitors, so it ends auto-advance.
   * @param {number} index
   */
  select(index) {
    this.#stopAutoAdvance();
    this.#show(index);
  }

  /**
   * @param {number} index
   */
  #show(index) {
    const { items, toggles, panels, slides, dots } = this.refs;
    const count = items.length;
    if (!count) return;

    const next = Math.min(count - 1, Math.max(0, Number(index) || 0));
    if (next === this.#active) return;

    const previous = this.#active;
    const prevItem = items[previous];
    const nextItem = items[next];
    const animate = Boolean(prevItem && nextItem) && !prefersReducedMotion();
    const desktop = mediaQueryDesktop.matches;

    // First: where every box is right now (mid-animation included)
    const before = animate ? items.map((item) => this.#measure(item)) : [];
    if (animate) this.#stopAnimations();

    this.#active = next;
    items.forEach((item, i) => {
      const isActive = i === next;
      item.toggleAttribute('data-active', isActive);
      toggles[i]?.setAttribute('aria-expanded', String(isActive));
      if (panels[i]) panels[i].hidden = !isActive;
      slides[i]?.toggleAttribute('data-active', isActive);
      dots?.[i]?.toggleAttribute('data-active', isActive);
    });

    this.#syncNav();
    this.#restartActiveVideo();
    this.#syncPlayback();

    if (!animate || !prevItem || !nextItem) return;

    // Last: read the final layout and animate from the old one
    const after = items.map((item) => this.#measure(item));

    if (desktop) {
      // The pill grows into the card in place; the old card shrinks back to a pill
      this.#morph(prevItem, before[previous], after[previous], COLLAPSE);
      this.#morph(nextItem, before[next], after[next], SPRING);
      this.#fadeOut(panels[previous], prevItem, before[previous].width);
    } else {
      // The card stays put and springs in with the new feature; the old one pops into the icon column
      // and the icons that changed place slide to their new slot
      nextItem.animate([{ transform: 'scale(0.94)' }, { transform: 'none' }], {
        duration: SPRING.duration,
        easing: SPRING.easing,
      });
      prevItem.animate([{ transform: 'scale(0.4)', opacity: 0 }, { transform: 'none', opacity: 1 }], {
        duration: SPRING.duration,
        easing: SPRING.easing,
      });
      items.forEach((item, i) => {
        if (i === next || i === previous) return;
        const dy = before[i].top - after[i].top;
        if (!dy) return;
        item.animate([{ transform: `translateY(${dy}px)` }, { transform: 'none' }], {
          duration: SPRING.duration,
          easing: SPRING.easing,
        });
      });
    }

    panels[next]?.animate(
      [
        { opacity: 0, transform: 'translateY(0.5rem) scale(0.98)', filter: 'blur(4px)' },
        { opacity: 1, transform: 'none', filter: 'blur(0)' },
      ],
      { duration: ANIMATION_DURATION, delay: 60, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'backwards' }
    );
  }

  previous() {
    this.select(this.#active - 1);
  }

  next() {
    this.select(this.#active + 1);
  }

  /** Pause or resume looping video (needed for motion longer than 5 seconds). */
  togglePlayback() {
    const { pauseButton } = this.refs;
    this.#userPaused = !this.#userPaused;
    this.#stopAutoAdvance();

    if (pauseButton) {
      pauseButton.toggleAttribute('data-paused', this.#userPaused);
      const label = this.#userPaused ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause;
      if (label) pauseButton.setAttribute('aria-label', label);
    }

    this.#syncPlayback();
  }

  /**
   * @param {HTMLElement} item
   * @returns {{ width: number, height: number, top: number, radius: number }}
   */
  #measure(item) {
    const { width, height, top } = item.getBoundingClientRect();
    // A pill's radius is 999rem; what you see is half its height
    const radius = Math.min(parseFloat(getComputedStyle(item).borderTopLeftRadius) || 0, height / 2);
    return { width, height, top, radius };
  }

  /**
   * Mobile: every card is as tall as the tallest one, so the media above keeps one height.
   * Briefly opens each feature (no paint happens in between) to read its natural height.
   */
  #reserveCardHeight() {
    const { list, items, panels } = this.refs;
    list.style.removeProperty('min-height');
    if (mediaQueryDesktop.matches || items.length < 2) return;

    list.toggleAttribute('data-measuring', true);
    let tallest = 0;
    items.forEach((item, i) => {
      items.forEach((other, j) => {
        other.toggleAttribute('data-active', i === j);
        if (panels[j]) panels[j].hidden = i !== j;
      });
      tallest = Math.max(tallest, item.getBoundingClientRect().height);
    });
    items.forEach((item, j) => {
      item.toggleAttribute('data-active', j === this.#active);
      if (panels[j]) panels[j].hidden = j !== this.#active;
    });
    list.removeAttribute('data-measuring');

    list.style.minHeight = `${Math.ceil(tallest)}px`;
  }

  /**
   * @param {HTMLElement} item
   * @param {{ width: number, height: number, top: number, radius: number }} from
   * @param {{ width: number, height: number, top: number, radius: number }} to
   * @param {{ easing: string, duration: number }} timing
   */
  #morph(item, from, to, timing) {
    item.animate(
      [
        { width: `${from.width}px`, height: `${from.height}px`, borderRadius: `${from.radius}px` },
        { width: `${to.width}px`, height: `${to.height}px`, borderRadius: `${to.radius}px` },
      ],
      { duration: timing.duration, easing: timing.easing }
    );
  }

  /**
   * Keeps the closing card's text visible for a moment, out of the flow, while its box shrinks.
   * @param {HTMLElement | undefined} panel
   * @param {HTMLElement} item
   * @param {number} width - The card's width before it started closing
   */
  #fadeOut(panel, item, width) {
    if (!panel) return;
    const toggle = item.querySelector('.feature-explorer__toggle');
    panel.style.top = `${toggle instanceof HTMLElement ? toggle.offsetHeight : 0}px`;
    panel.style.width = `${width}px`;
    panel.toggleAttribute('data-leaving', true);
    panel.hidden = false;

    const fade = panel.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 160,
      easing: 'ease-out',
      fill: 'forwards',
    });
    fade.onfinish = () => this.#cleanUpPanel(panel);
    // Animations stall in a background tab; don't leave the panel stranded if `finish` never fires
    setTimeout(() => this.#cleanUpPanel(panel), 400);
  }

  /** @param {HTMLElement} panel */
  #cleanUpPanel(panel) {
    if (!panel.hasAttribute('data-leaving')) return;
    for (const animation of panel.getAnimations()) animation.cancel();
    panel.removeAttribute('data-leaving');
    panel.style.removeProperty('top');
    panel.style.removeProperty('width');
    panel.hidden = this.refs.panels.indexOf(panel) !== this.#active;
  }

  /** Ends any running open/close animation so a new one starts from what's on screen. */
  #stopAnimations() {
    const { items, panels } = this.refs;
    panels.forEach((panel) => this.#cleanUpPanel(panel));
    for (const el of [...items, ...panels]) {
      for (const animation of el.getAnimations()) animation.cancel();
    }
  }

  /**
   * Prev shrinks away on the first feature, next on the last, on the same spring as the cards.
   * @param {boolean} [animate]
   */
  #syncNav(animate = true) {
    const { prevButton, nextButton, items } = this.refs;
    const atStart = this.#active === 0;
    const atEnd = this.#active === items.length - 1;

    // Don't strand keyboard focus on a button that's about to disappear
    if (atStart && document.activeElement === prevButton) nextButton?.focus({ preventScroll: true });
    if (atEnd && document.activeElement === nextButton) prevButton?.focus({ preventScroll: true });

    this.#setNavHidden(prevButton, atStart, animate);
    this.#setNavHidden(nextButton, atEnd, animate);
  }

  /**
   * @param {HTMLButtonElement | undefined} button
   * @param {boolean} hidden
   * @param {boolean} animate
   */
  #setNavHidden(button, hidden, animate) {
    if (!button || button.disabled === hidden) return;
    button.disabled = hidden;
    if (!animate || prefersReducedMotion()) return;

    // The disabled styles (scale 0, hidden) apply at once; keep it visible while it animates out
    const shown = { scale: 1, opacity: 1, visibility: 'visible' };
    const gone = { scale: 0, opacity: 0, visibility: 'visible' };
    button.animate(hidden ? [shown, gone] : [gone, shown], hidden ? BOUNCE_OUT : SPRING);
  }

  /** Arrow keys move between pills, like a tab list. */
  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const { toggles } = this.refs;
    const current = toggles.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (current === -1) return;

    /** @type {number | null} */
    let target = null;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        target = current + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        target = current - 1;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = toggles.length - 1;
        break;
    }
    if (target === null) return;

    event.preventDefault();
    this.select(target);
    toggles[this.#active]?.focus({ preventScroll: true });
  };

  /** Mobile: a sideways swipe on the media or the cards opens the next or previous feature. */
  #onTouchStart = (/** @type {TouchEvent} */ event) => {
    const touch = event.touches[0];
    this.#touchStart = touch && !mediaQueryDesktop.matches ? { x: touch.clientX, y: touch.clientY } : null;
  };

  #onTouchEnd = (/** @type {TouchEvent} */ event) => {
    const start = this.#touchStart;
    const touch = event.changedTouches[0];
    this.#touchStart = null;
    if (!start || !touch) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;

    if (dx < 0) this.next();
    else this.previous();
  };

  #onBlockSelect = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const index = this.refs.items.findIndex((item) => item === target || item.contains(target));
    if (index !== -1) this.select(index);
  };

  /** The active video finished: move on, wrapping from the last feature to the first */
  #onVideoEnded = (/** @type {Event} */ event) => {
    if (!this.#autoAdvance || event.target !== this.refs.slides[this.#active]?.querySelector('video')) return;
    this.#show((this.#active + 1) % this.refs.items.length);
  };

  #stopAutoAdvance() {
    this.#autoAdvance = false;
    clearTimeout(this.#imageTimer);
  }

  /** Image features have no end, so they hold for a few seconds while the section is on screen */
  #syncImageTimer() {
    clearTimeout(this.#imageTimer);
    const hasVideo = Boolean(this.refs.slides[this.#active]?.querySelector('video'));
    if (!this.#autoAdvance || hasVideo || !this.#inView || this.#userPaused) return;

    this.#imageTimer = setTimeout(() => this.#show((this.#active + 1) % this.refs.items.length), IMAGE_HOLD);
  }

  /** @returns {HTMLVideoElement[]} */
  get #videos() {
    return this.refs.slides.map((slide) => slide.querySelector('video')).filter((v) => v !== null);
  }

  #restartActiveVideo() {
    const video = this.refs.slides[this.#active]?.querySelector('video');
    if (video) video.currentTime = 0;
  }

  /** Plays only the active video, only while the section is on screen and motion is allowed. */
  #syncPlayback() {
    this.#syncImageTimer();
    const activeVideo = this.refs.slides[this.#active]?.querySelector('video');

    for (const video of this.#videos) {
      if (video !== activeVideo) video.pause();
    }
    if (!activeVideo) return;

    // PERF-07: in the lite tier (phones by default) and with reduced motion the video's poster is the design; no
    // video bytes are fetched and nothing plays.
    if (!canGoLive() || prefersReducedMotion()) {
      activeVideo.pause();
      return;
    }

    if (activeVideo.preload === 'none') activeVideo.preload = 'auto';

    // A play-once video that has finished holds its last frame until the feature is chosen again
    if (activeVideo.ended && !activeVideo.loop) return;

    if (this.#inView && !this.#userPaused && !prefersReducedMotion()) {
      activeVideo.play().catch(() => {});
    } else {
      activeVideo.pause();
    }
  }
}

if (!customElements.get('feature-explorer-component')) {
  customElements.define('feature-explorer-component', FeatureExplorerComponent);
}
